import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminShell from '../../components/AdminShell.jsx';
import Alert from '../../components/Alert.jsx';
import GrantsMatrix from '../../components/GrantsMatrix.jsx';
import { api } from '../../api/client.js';

function generatePassword() {
  // 16 base32-friendly chars. No ambiguous characters (0/O, 1/I).
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

export default function UserCreate() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [grants, setGrants] = useState(new Set());
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    // adminListPackages returns every package (incl. those with 0 overlays
    // defined yet) so the grants matrix can show FIFA 22 etc. Use it instead
    // of myPackages so we don't accidentally hide newly added games.
    api.adminListPackages()
      .then((data) => { if (alive) setPackages(data?.packages ?? []); })
      .catch((e) => { if (alive) setError(e.message); });
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await api.adminCreateUser({
        username: username.trim(),
        password,
        role,
        overlayIds: [...grants],
      });
      navigate(`/admin/users/${resp.user.id}`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AdminShell crumb="New User">
      <div className="adm-form-col">
        <header className="adm-head adm-rv" style={{ animationDelay: '80ms' }}>
          <h1 className="adm-head__title">
            <span className="section-marker" aria-hidden="true">◆</span> New User
          </h1>
          <p className="adm-head__sub">
            Creates the account and its overlay key. The password is shown in plaintext so you
            can hand it over.
          </p>
        </header>

        {error && <Alert kind="danger" onClose={() => setError(null)}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          <div className="card mb-3">
            <div className="card-header adm-card-label">Identity</div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-control"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Password <span className="text-muted small">(8+ chars)</span>
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control font-monospace"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setPassword(generatePassword())}
                    >
                      Generate
                    </button>
                  </div>
                  <div className="form-text">
                    Shown in plaintext so you can copy and hand it to the user.
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header adm-card-label">Overlay grants</div>
            <div className="card-body">
              {role === 'admin' ? (
                <p className="text-muted small mb-3">
                  Admins implicitly have access to every overlay; grants below are
                  saved but redundant.
                </p>
              ) : (
                <p className="text-muted small mb-3">
                  Tick the overlays this user can use. Each tick is one row in
                  <code> user_overlays</code>.
                </p>
              )}
              <GrantsMatrix
                packages={packages}
                value={grants}
                onChange={setGrants}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Link to="/admin/users" className="btn btn-outline-secondary">
              Cancel
            </Link>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create user'}
            </button>
          </div>
        </form>
      </div>
    </AdminShell>
  );
}
